"use client";

import { excerptWithoutTitle } from "@/lib/blogArticle";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Container } from "@/components/Container";
import { Reveal, RevealGroup, RevealItem } from "@/components/Motion";
import type { MediaItem } from "@/lib/contentDb";
import type { Video } from "@/lib/videosDb";

const TABS = ["Articles", "Events", "Videos"] as const;
type Tab = (typeof TABS)[number];

const TYPE_LABELS: Record<string, string> = {
  article: "Article",
  "press-release": "Press release",
  news: "News",
  award: "Award",
  event: "Event",
};

function fmtDate(date: string | null): string | null {
  if (!date) return null;
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function fmtMonth(date: string | null): string | null {
  if (!date) return null;
  // Accepts a bare YYYY-MM-DD (media rows) or a full ISO timestamp (YouTube
  // publish times) — appending the time to an ISO string made it unparseable
  // and the raw value leaked into the card.
  const d = new Date(date.length > 10 ? date : `${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-IN", { month: "long", year: "numeric", timeZone: "UTC" });
}

/* -------------------------------------------------------------- cards */

function ArticleCard({ item }: { item: MediaItem }) {
  const meta = [fmtDate(item.date), item.source].filter(Boolean).join(" · ");
  return (
    <article className="flex h-full flex-col">
      {item.image && (
        <div className="overflow-hidden rounded-2xl ring-1 ring-brand-line">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={item.image}
            alt={item.title}
            loading="lazy"
            className="aspect-[3/2] w-full object-cover"
          />
        </div>
      )}
      <div className="mt-5 flex items-baseline gap-3">
        <span className="mono-meta text-brand-accent-dark">
          {TYPE_LABELS[item.type] ?? item.type}
        </span>
        {meta && <span className="mono-meta text-brand-muted">{meta}</span>}
      </div>
      <h3 className="t-title mt-3 text-brand-ink">{item.title}</h3>
      {excerptWithoutTitle(item.excerpt, item.title) && (
        <p className="mt-3 max-w-xl text-brand-slate">{excerptWithoutTitle(item.excerpt, item.title)}</p>
      )}
      {item.slug ? (
        <p className="mt-4">
          <Link
            href={`/media/${item.slug}`}
            className="text-sm font-medium text-brand-accent-dark transition-colors hover:text-brand-ink"
          >
            Read &rarr;
          </Link>
        </p>
      ) : item.url ? (
        <p className="mt-4">
          <a
            href={item.url}
            target="_blank"
            rel="noopener"
            className="text-sm font-medium text-brand-accent-dark transition-colors hover:text-brand-ink"
          >
            Read &rarr;
          </a>
        </p>
      ) : null}
    </article>
  );
}

function EventCard({ item }: { item: MediaItem }) {
  return (
    <article className="group flex h-full flex-col">
      {item.image && (
        <div className="overflow-hidden rounded-2xl ring-1 ring-brand-line">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={item.image}
            alt={item.title}
            loading="lazy"
            className="aspect-[4/3] w-full object-cover grayscale transition duration-700 group-hover:grayscale-0"
          />
        </div>
      )}
      {item.date && (
        <p className="mono-meta mt-5 uppercase text-brand-accent-dark">
          {fmtMonth(item.date)}
        </p>
      )}
      <h3 className="t-title mt-2 text-brand-ink">{item.title}</h3>
      {excerptWithoutTitle(item.excerpt, item.title) && (
        <p className="mt-3 text-brand-slate">{excerptWithoutTitle(item.excerpt, item.title)}</p>
      )}
      {item.slug && (
        <p className="mt-4">
          <Link
            href={`/media/${item.slug}`}
            className="text-sm font-medium text-brand-accent-dark transition-colors hover:text-brand-ink"
          >
            Read &rarr;
          </Link>
        </p>
      )}
    </article>
  );
}

/** A video links out to YouTube rather than embedding: an iframe per card
 *  would load YouTube's player (and its cookies) for every visitor who merely
 *  scrolls past the tab. The thumbnail is served from YouTube's image host. */
function VideoCard({ item }: { item: Video }) {
  return (
    <article className="group h-full">
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block"
        aria-label={`Watch "${item.title}" on YouTube`}
      >
        <div className="relative aspect-video w-full overflow-hidden rounded-2xl ring-1 ring-brand-line">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={item.thumbnail}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          />
          <span className="absolute inset-0 grid place-items-center">
            <span className="grid h-14 w-14 place-items-center rounded-full bg-brand-ink/70 backdrop-blur-sm transition-colors group-hover:bg-brand-accent">
              <svg viewBox="0 0 24 24" className="ml-0.5 h-6 w-6 fill-white" aria-hidden>
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </span>
        </div>
      </a>
      {item.publishedAt && (
        <p className="mono-meta mt-5 uppercase text-brand-accent-dark">
          {fmtMonth(item.publishedAt)}
        </p>
      )}
      <h3 className="t-h3 mt-2 text-brand-ink">{item.title}</h3>
      <p className="mt-4">
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-brand-accent-dark transition-colors hover:text-brand-ink"
        >
          Watch on YouTube &rarr;
        </a>
      </p>
    </article>
  );
}

/* -------------------------------------------------------------- tabs */

export function MediaTabs({
  articles,
  events,
  videos = [],
}: {
  articles: MediaItem[];
  events: MediaItem[];
  /** Latest uploads from the Table Space YouTube channel. */
  videos?: Video[];
}) {
  const [active, setActive] = useState<Tab>("Articles");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 9;

  // Deep-link support: /media#events opens the Events tab (used by nav/footer).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash.toLowerCase();
    if (hash === "#events") setActive("Events");
    else if (hash === "#videos") setActive("Videos");
  }, []);

  const select = (t: Tab) => {
    setActive(t);
    setPage(1);
    if (typeof window !== "undefined") {
      const hash = t === "Events" ? "#events" : t === "Videos" ? "#videos" : " ";
      history.replaceState(null, "", hash);
    }
  };

  const items: (MediaItem | Video)[] =
    active === "Articles" ? articles : active === "Events" ? events : videos;
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const paged = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const goTo = (p: number) => {
    setPage(p);
    if (typeof window !== "undefined") {
      document
        .getElementById("newsroom")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section id="newsroom" className="scroll-mt-24 bg-brand-paper py-24 md:py-36">
      <Container>
        <Reveal>
          <div className="flex">
            {/* segmented tab control — paper track, teal active pill */}
            <nav
              aria-label="Newsroom sections"
              className="inline-flex shrink-0 gap-1 self-start rounded-full bg-brand-surface p-1.5 ring-1 ring-brand-line"
            >
              {TABS.map((t) => {
                const isActive = t === active;
                const count =
                  t === "Articles" ? articles.length : t === "Events" ? events.length : videos.length;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => select(t)}
                    aria-current={isActive ? "true" : undefined}
                    className={`rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300 ${
                      isActive
                        ? "bg-brand-accent text-brand-paper shadow-[0_8px_20px_-10px_rgba(56,105,99,0.65)]"
                        : "text-brand-slate hover:text-brand-accent"
                    }`}
                  >
                    {t}
                    <span className={isActive ? "ml-2 text-brand-paper/70" : "ml-2 text-brand-muted"}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>
        </Reveal>

        {items.length === 0 ? (
          <p className="mt-14 text-brand-slate">Nothing here yet — check back soon.</p>
        ) : (
          <>
            <RevealGroup
              key={`${active}-${current}`}
              className="mt-14 grid gap-x-10 gap-y-16 sm:grid-cols-2 lg:grid-cols-3"
            >
              {paged.map((item) => (
                <RevealItem key={item.id} className="h-full">
                  {/* The active tab decides the shape of its own items. */}
                  {active === "Videos" ? (
                    <VideoCard item={item as Video} />
                  ) : active === "Events" ? (
                    <EventCard item={item as MediaItem} />
                  ) : (
                    <ArticleCard item={item as MediaItem} />
                  )}
                </RevealItem>
              ))}
            </RevealGroup>

            {totalPages > 1 && (
              <nav
                aria-label="Pagination"
                className="mt-16 flex items-center justify-center gap-2"
              >
                <button
                  type="button"
                  onClick={() => goTo(current - 1)}
                  disabled={current === 1}
                  aria-label="Previous page"
                  className="rounded-full border border-brand-line px-4 py-2 text-sm font-medium text-brand-slate transition-colors hover:border-brand-accent hover:text-brand-accent-dark disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-brand-line disabled:hover:text-brand-slate"
                >
                  ← Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => goTo(p)}
                    aria-current={p === current ? "page" : undefined}
                    className={`h-10 w-10 rounded-full text-sm font-medium transition-colors ${
                      p === current
                        ? "bg-brand-accent text-brand-paper"
                        : "border border-brand-line text-brand-slate hover:border-brand-accent hover:text-brand-accent-dark"
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => goTo(current + 1)}
                  disabled={current === totalPages}
                  aria-label="Next page"
                  className="rounded-full border border-brand-line px-4 py-2 text-sm font-medium text-brand-slate transition-colors hover:border-brand-accent hover:text-brand-accent-dark disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-brand-line disabled:hover:text-brand-slate"
                >
                  Next →
                </button>
              </nav>
            )}
          </>
        )}
      </Container>
    </section>
  );
}
